"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { X } from "lucide-react";
import posthog from "posthog-js";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { getCountryDisplayName } from "@/lib/geo/country-display-name";
import { resolveAmericasSwitchTarget, switchChannelPath } from "@/lib/geo/switch-channel-path";
import { useLocaleChannelState } from "./use-locale-channel-state";
import { suppressBrazilCalculatorRegionSuggestion } from "@/lib/geo/brazil-language-suggestion";
import { usePreferredBrowserLanguage } from "./use-preferred-browser-language";

function setMismatchDismissedCookie(country: string, channel: string) {
	if (typeof document === "undefined") return;
	const maxAge = 60 * 60 * 24 * 30; // 30 days
	const secure = typeof location !== "undefined" && location.protocol === "https:";
	document.cookie = `mismatch_dismissed=${country}-${channel}; Path=/; Max-Age=${maxAge}; SameSite=Lax${
		secure ? "; Secure" : ""
	}`;
}

export function ChannelMismatchBanner() {
	const t = useTranslations("channelMismatch");
	const router = useRouter();
	const pathname = usePathname();
	const state = useLocaleChannelState();
	const [hidden, setHidden] = useState(false);
	const preferredBrowserLanguage = usePreferredBrowserLanguage();

	const shouldRender =
		!suppressBrazilCalculatorRegionSuggestion(pathname, state.detectedCountry, preferredBrowserLanguage) &&
		state.hasGeoPrefCookie &&
		!state.mismatchDismissed &&
		!hidden &&
		(state.mismatch.kind === "suggest-americas" || state.mismatch.kind === "suggest-worldwide");

	const mismatch = state.mismatch;
	const detectedCountry =
		mismatch.kind === "suggest-americas" || mismatch.kind === "suggest-worldwide"
			? mismatch.detectedCountry
			: "";
	const countryName = getCountryDisplayName(detectedCountry, state.currentLocale);

	useEffect(() => {
		if (!shouldRender) return;
		posthog.capture("channel_mismatch_suggested", {
			source: "banner",
			fromChannel: state.currentChannel,
			suggestedChannel: mismatch.kind === "suggest-americas" ? "us" : "eu",
			detectedCountry,
		});
	}, [shouldRender, state.currentChannel, mismatch.kind, detectedCountry]);

	const handleCta = useCallback(() => {
		if (mismatch.kind === "suggest-americas") {
			const target = resolveAmericasSwitchTarget(pathname, state.currentLocale, mismatch.detectedCountry);
			setGeoPrefCookie({
				locale: target.locale,
				channel: "us",
				referenceCurrency: state.referenceCurrency,
			});
			router.push(target.path);
		} else if (mismatch.kind === "suggest-worldwide") {
			setGeoPrefCookie({
				locale: state.currentLocale,
				channel: "eu",
				referenceCurrency: state.referenceCurrency,
			});
			router.push(switchChannelPath(pathname, "eu"));
		}
	}, [mismatch, state, router, pathname]);

	const handleDismiss = useCallback(() => {
		setMismatchDismissedCookie(detectedCountry, state.currentChannel);
		setHidden(true);
	}, [detectedCountry, state.currentChannel]);

	if (!shouldRender) return null;

	const message =
		mismatch.kind === "suggest-americas"
			? t("toAmericas", { country: countryName })
			: t("toWorldwide", { country: countryName });

	const ctaLabel = mismatch.kind === "suggest-americas" ? t("ctaAmericas") : t("ctaWorldwide");

	return (
		<div
			role="region"
			aria-label="Region suggestion"
			className="flex w-full items-center gap-3 border-b border-[#dff0e7] bg-gradient-to-r from-[#eff7f3] to-[#f3faf6] px-4 py-2.5 text-sm text-foreground sm:px-6"
		>
			<span className="text-base" aria-hidden>
				🌍
			</span>
			<p className="flex-1 leading-snug">{message}</p>
			<button
				type="button"
				onClick={handleCta}
				className="hover:bg-primary/90 inline-flex min-h-9 items-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm transition-colors"
			>
				{ctaLabel}
			</button>
			<button
				type="button"
				onClick={handleDismiss}
				aria-label={t("dismissAria")}
				className="hover:bg-background/60 inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
